import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Typography,
    Grid,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function EditLearningPathPage() {
    const navigate = useNavigate();
    const { pathId } = useParams();
    const [path, setPath] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [openLevelDialog, setOpenLevelDialog] = useState(false);
    const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
    const [editingLevelIndex, setEditingLevelIndex] = useState(null);
    const [levelForm, setLevelForm] = useState({
        name: "",
        description: "",
        duration: "30 mins",
        qualifyingScore: 70,
        questions: []
    });
    const [questionForm, setQuestionForm] = useState({
        text: "",
        options: ["", "", "", ""],
        correctAnswer: 0
    });

    useEffect(() => {
        const fetchPath = async () => {
            try {
                setLoading(true);
                const pathData = await learningService.getLearningPathById(pathId);
                setPath(pathData);
                setFormData({
                    name: pathData?.name || "",
                    description: pathData?.description || "",
                    difficulty: pathData?.difficulty || "Beginner",
                    creditsOnCompletion: pathData?.creditsOnCompletion || 10,
                    duration: pathData?.duration || "4 weeks"
                });
            } catch (error) {
                setMessage("Error loading path");
            } finally {
                setLoading(false);
            }
        };

        fetchPath();
    }, [pathId]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await learningService.updateLearningPath(pathId, {
                ...formData,
                levels: path?.levels || []
            });
            setMessage("Path updated successfully!");
        } catch (error) {
            setMessage(error.message || "Failed to update path");
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => {
        if (!questionForm.text || questionForm.options.some((o) => !o)) {
            setMessage("Please fill all question fields");
            return;
        }

        const questions = [...(levelForm.questions || [])];
        questions.push(questionForm);
        setLevelForm((prev) => ({
            ...prev,
            questions
        }));

        setQuestionForm({
            text: "",
            options: ["", "", "", ""],
            correctAnswer: 0
        });
        setOpenQuestionDialog(false);
    };

    const addLevel = () => {
        if (!levelForm.name || levelForm.questions.length === 0) {
            setMessage("Please fill level name and add at least one question");
            return;
        }

        const newLevel = {
            id: `level_${Date.now()}`,
            ...levelForm,
            order: (path?.levels?.length || 0) + 1
        };

        const levels = [...(path?.levels || [])];
        levels.push(newLevel);
        setPath((prev) => ({
            ...prev,
            levels
        }));

        setLevelForm({
            name: "",
            description: "",
            duration: "30 mins",
            qualifyingScore: 70,
            questions: []
        });
        setOpenLevelDialog(false);
    };

    const removeLevel = (levelId) => {
        const levels = path?.levels?.filter((l) => l.id !== levelId) || [];
        setPath((prev) => ({
            ...prev,
            levels
        }));
    };

    if (loading) {
        return (
            <AdminLayout title="Edit Learning Path">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Edit Learning Path">
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Edit Learning Path
                </Typography>

                {message && (
                    <Alert
                        severity={message.includes("successfully") ? "success" : "error"}
                        sx={{ mb: 2 }}
                        onClose={() => setMessage("")}
                    >
                        {message}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Path Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleFormChange}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleFormChange}
                                    multiline
                                    rows={3}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    select
                                    label="Difficulty"
                                    name="difficulty"
                                    value={formData.difficulty}
                                    onChange={handleFormChange}
                                >
                                    <MenuItem value="Beginner">Beginner</MenuItem>
                                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                                    <MenuItem value="Advanced">Advanced</MenuItem>
                                </TextField>

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    type="number"
                                    label="Credits on Completion"
                                    name="creditsOnCompletion"
                                    value={formData.creditsOnCompletion}
                                    onChange={handleFormChange}
                                />

                                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? <CircularProgress size={22} /> : "Save Changes"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate("/admin/learning-paths")}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Levels
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        size="small"
                                        onClick={() => {
                                            setEditingLevelIndex(null);
                                            setLevelForm({
                                                name: "",
                                                description: "",
                                                duration: "30 mins",
                                                qualifyingScore: 70,
                                                questions: []
                                            });
                                            setOpenLevelDialog(true);
                                        }}
                                    >
                                        Add Level
                                    </Button>
                                </Box>

                                {path?.levels?.length > 0 ? (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "primary.lighter" }}>
                                                <TableCell>Name</TableCell>
                                                <TableCell align="right">Q</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {path.levels.map((level) => (
                                                <TableRow key={level.id}>
                                                    <TableCell>{level.name}</TableCell>
                                                    <TableCell align="right">
                                                        {level.questions?.length || 0}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeLevel(level.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Alert severity="info">No levels added</Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Level Dialog */}
            <Dialog open={openLevelDialog} onClose={() => setOpenLevelDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingLevelIndex !== null ? "Edit Level" : "Add New Level"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Level Name"
                            value={levelForm.name}
                            onChange={(e) =>
                                setLevelForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            type="number"
                            label="Qualifying Score (%)"
                            value={levelForm.qualifyingScore}
                            onChange={(e) =>
                                setLevelForm((prev) => ({
                                    ...prev,
                                    qualifyingScore: parseInt(e.target.value)
                                }))
                            }
                            inputProps={{ min: 0, max: 100 }}
                        />

                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="subtitle2">
                                    Questions ({levelForm.questions?.length || 0})
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenQuestionDialog(true)}
                                >
                                    Add Question
                                </Button>
                            </Box>

                            {levelForm.questions?.map((q, index) => (
                                <Card key={index} sx={{ mb: 1, p: 1 }}>
                                    <Typography variant="body2">
                                        <strong>Q{index + 1}:</strong> {q.text}
                                    </Typography>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLevelDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addLevel}>
                        {editingLevelIndex !== null ? "Update" : "Add"} Level
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Question Dialog */}
            <Dialog
                open={openQuestionDialog}
                onClose={() => setOpenQuestionDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Question</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Question Text"
                            value={questionForm.text}
                            onChange={(e) =>
                                setQuestionForm((prev) => ({ ...prev, text: e.target.value }))
                            }
                            multiline
                            rows={2}
                        />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                            Options
                        </Typography>
                        {questionForm.options.map((option, index) => (
                            <TextField
                                key={index}
                                fullWidth
                                margin="normal"
                                label={`Option ${index + 1}`}
                                value={option}
                                onChange={(e) => {
                                    const options = [...questionForm.options];
                                    options[index] = e.target.value;
                                    setQuestionForm((prev) => ({ ...prev, options }));
                                }}
                                size="small"
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenQuestionDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addQuestion}>
                        Add Question
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
